import React, { useRef, useState } from "react";
import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";
import {
	avoidsState,
	costingState,
	currentDirections,
	calibrationsStateAtoms,
	editingAvoidsState,
	isCalibratedAtom,
} from "./state";
import Map, { AvoidedLine, DirectionLayer } from "./Map";
import Control from "./Control";
import FromToEditor, { AutocompleteSet } from "./FromToEditor";
import ListEditor from "./ListEditor";
import { Directions } from "./primitives";

export default () => {
	const [current, setCurrent] = useAtom(currentDirections);
	const [calibrationAtoms, dispatch] = useAtom(calibrationsStateAtoms);
	const [avoids] = useAtom(avoidsState);
	const clearTemporary = () => setCurrent(null);
	const mapRef = useRef<L.Map>(null);
	const [editingAvoids, setEditingAvoids] = useAtom(editingAvoidsState);
	const toggleAvoids = () => setEditingAvoids((prev) => !prev);
	const persist = () => {
		if (current) {
			dispatch({
				type: "insert",
				value: current,
			});
			calibrate(true);
		}
	};
	const [isCalibrated] = useAtom(isCalibratedAtom);
	const [calibrating, calibrate] = useState(false);
	const updateCosting = useSetAtom(costingState);
	const jeffsPrefs = () =>
		updateCosting({
			use_bikepaths: 0,
			use_oneways: 0.5,
			use_roads: 0,
		});

	let mapped: PrimitiveAtom<Directions>[] = [];
	if (calibrating) {
		mapped = calibrationAtoms;
	} else if (current) {
		mapped = [currentDirections as PrimitiveAtom<Directions>];
	}
	let routeButton: JSX.Element | undefined;
	if (current) {
		if (isCalibrated) {
			routeButton = (
				<button
					data-button-toggled={calibrating}
					className="button toggler"
					onClick={() => calibrate((v) => !v)}
				>
					calibrate
				</button>
			);
		} else {
			routeButton = (
				<button className="button" onClick={persist}>
					add to calibration routes
				</button>
			);
		}
	}
	const [mobileShown, setMobileShown] = useState(false);

	return (
		<div>
			<Map editingAvoids={editingAvoids} ref={mapRef}>
				<>
					{mapped.map((dir) => (
						<DirectionLayer key={dir.toString()} directionsAtom={dir} />
					))}
					{avoids.map((avoid) => (
						<AvoidedLine key={avoid.polyline} avoid={avoid} />
					))}
				</>
			</Map>
			{calibrationAtoms.length ? <ListEditor toggled={calibrating} /> : null}
			{editingAvoids && (
				<p id="avoids-instructions">
					Tap on a street to turn it <span style={{ color: "red" }}>red</span>{" "}
					and these directions will always avoid it for you. Tap it again to
					reinstate it.
				</p>
			)}
			<div id="form" data-peeking={mobileShown}>
				<div id="directions-input">
					<FromToEditor
						result={current}
						mapRef={mapRef}
						onClear={() => {
							clearTemporary();
						}}
						onAdd={(result: AutocompleteSet) => {
							setCurrent({
								from: result.from.latlng,
								fromLabel: result.from.text,
								to: result.to.latlng,
								toLabel: result.to.text,
							});
						}}
					/>
				</div>
				<div id="controls-set">
					<button
						className="mobile-hider"
						onClick={() => setMobileShown((s) => !s)}
					>
						peek me
					</button>
					<div className="controls">
						<Control
							costing_option={"use_roads"}
							label="cars"
							zero_label="hate em"
							hundo_label="love em"
						/>
						<Control
							costing_option={"use_oneways"}
							label="one-way streets"
							zero_label="neutral"
							hundo_label="love em"
							neutral
						/>
						<Control
							costing_option={"use_bikepaths"}
							label="separated bike paths"
							zero_label="love em"
							hundo_label="neutral"
							neutral
							invert
						/>
					</div>
					<section className="buttons">
						<button
							aria-label="use Jeff's prefs"
							className="button"
							id="jeffs-prefs"
							onClick={jeffsPrefs}
						>
							<img src="jeffs-prefs.png" width="157.5" height="26.5" />
						</button>
						<button
							data-button-toggled={editingAvoids}
							onClick={toggleAvoids}
							className="toggler button"
						>
							edit avoided roads ✏️
						</button>
					</section>
				</div>
			</div>
		</div>
	);
};
